import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MyReviewDto, updateReview, deleteReview } from '../api/review';



interface StarPickerProps {
  value: number;
  onChange: (v: number) => void;
}

const StarPicker: React.FC<StarPickerProps> = ({ value, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`text-lg leading-none transition-colors ${n <= value ? 'text-yellow-400' : 'text-outline-variant'}`}
      >
        ★
      </button>
    ))}
  </div>
);

function avgRating(r: MyReviewDto) {
  return Math.round((r.qualityRating + r.reliabilityRating + r.communicationRating + r.pricingRating) / 4);
}

interface Props {
  reviews: MyReviewDto[];
  onReviewDeleted: (reviewId: number) => void;
  onReviewUpdated: (review: MyReviewDto) => void;
}

const MyRatingsReviewsSection: React.FC<Props> = ({ reviews, onReviewDeleted, onReviewUpdated }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editFields, setEditFields] = useState<Partial<MyReviewDto>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = (r: MyReviewDto) => {
    setEditingId(r.id);
    setEditFields({
      qualityRating: r.qualityRating,
      reliabilityRating: r.reliabilityRating,
      communicationRating: r.communicationRating,
      pricingRating: r.pricingRating,
      comment: r.comment,
    });
    setError('');
  };

  const cancelEdit = () => { setEditingId(null); setError(''); };

  const handleSaveEdit = async (r: MyReviewDto) => {
    setSaving(true);
    setError('');
    try {
      await updateReview(r.id, {
        qualityRating: editFields.qualityRating ?? r.qualityRating,
        reliabilityRating: editFields.reliabilityRating ?? r.reliabilityRating,
        communicationRating: editFields.communicationRating ?? r.communicationRating,
        pricingRating: editFields.pricingRating ?? r.pricingRating,
        comment: editFields.comment ?? r.comment,
      });
      onReviewUpdated({ ...r, ...editFields } as MyReviewDto);
      setEditingId(null);
    } catch {
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reviewId: number) => {
    setDeletingId(reviewId);
    try {
      await deleteReview(reviewId);
      onReviewDeleted(reviewId);
    } catch {
      setError('Delete failed. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="p-6">
      <h2 className="font-title-lg text-on-surface mb-4">My reviews</h2>
      {reviews.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No reviews submitted yet. Reviews unlock once a job is completed.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map(r => {
            const avg = avgRating(r);
            const isEditing = editingId === r.id;
            const isDeleting = deletingId === r.id;

            return (
              <div key={r.id} className="rounded-xl border border-outline-variant bg-surface-container p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <Link
                      to={`/contractors/${r.contractorProfileId}`}
                      className="text-sm font-semibold text-primary hover:underline truncate block"
                    >
                      {r.contractorName}
                    </Link>
                    <Link to={`/jobs/${r.jobId}`} className="text-xs text-on-surface-variant hover:underline truncate block">
                      {r.jobTitle}
                    </Link>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-yellow-400 text-sm">{'★'.repeat(avg)}{'☆'.repeat(5 - avg)}</span>
                    <span className="text-xs text-on-surface-variant ml-1">
                      {new Date(r.timestamp).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      {(['qualityRating', 'reliabilityRating', 'communicationRating', 'pricingRating'] as const).map(field => (
                        <div key={field} className="flex items-center justify-between">
                          <span className="text-xs text-on-surface-variant capitalize">{field.replace('Rating', '')}</span>
                          <StarPicker
                            value={editFields[field] ?? r[field]}
                            onChange={v => setEditFields(f => ({ ...f, [field]: v }))}
                          />
                        </div>
                      ))}
                    </div>
                    <textarea
                      className="w-full px-3 py-2 text-sm border border-outline-variant rounded-lg bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      rows={3}
                      value={editFields.comment ?? r.comment}
                      onChange={e => setEditFields(f => ({ ...f, comment: e.target.value }))}
                    />
                    {error && <p className="text-xs text-error">{error}</p>}
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-1.5 text-sm text-on-surface-variant border border-outline-variant rounded-lg hover:bg-surface-container-high transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveEdit(r)}
                        disabled={saving}
                        className="px-4 py-1.5 text-sm bg-primary text-on-primary rounded-lg disabled:opacity-50 hover:brightness-110 transition-all"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-on-surface-variant mt-1">"{r.comment}"</p>
                    <div className="flex gap-3 mt-3 justify-end">
                      <button
                        onClick={() => startEdit(r)}
                        className="text-xs text-primary hover:underline font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        disabled={isDeleting}
                        className="text-xs text-error hover:underline font-medium disabled:opacity-50"
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default MyRatingsReviewsSection;
